import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../hooks/useAuth';
import { isTokenExpired } from '@shared/utils/jwt';
import { handleSessionExpired } from '@shared/utils/authSession';
import { saveRedirectPath } from '@shared/utils/redirectAfterLogin';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  requiredAnyPermissions?: string[];
  requiredRole?: string;
}

export function ProtectedRoute({ 
  children, 
  requiredPermission, 
  requiredAnyPermissions,
  requiredRole 
}: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAuthStore();
  const { hasPermission } = useAuth();
  const location = useLocation();
  const { message } = App.useApp();
  const { t } = useTranslation(['auth']);

  // Проверить истечение токена
  useEffect(() => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (token && isTokenExpired(token)) {
      // Сохранить текущий путь для редиректа после входа
      saveRedirectPath();
      
      // Обработать истечение сессии
      handleSessionExpired('expired', {
        messageApi: message,
        t,
        showNotification: true,
        redirectToLogin: true,
      });
    }
  }, [location.pathname, message, t]);

  // Если не аутентифицирован - перенаправить на login
  if (!isAuthenticated) {
    // Сохранить текущий путь для редиректа после входа
    saveRedirectPath();
    return <Navigate to="/login" replace />;
  }

  // Проверить разрешение (если требуется).
  //
  // Используем hasPermission из useAuth, а НЕ прямой user.permissions.includes:
  // это единственная реализация, знающая про обход администратора, и та же,
  // по которой экраны решают, показывать ли кнопки. Прямая проверка списка
  // расходилась и с бэкендом, и с самими экранами: реальный админ может
  // не нести точечное право в permissions, но имеет его по роли;
  // backend middleware пропускает админа по роли, useAuth.hasPermission — тоже;
  // прямая проверка user.permissions разошлась бы с обеими границами.
  if (requiredPermission && user) {
    if (!hasPermission(requiredPermission)) {
      return <Navigate to="/" replace />;
    }
  }

  if (requiredAnyPermissions && !requiredAnyPermissions.some(hasPermission)) {
    return <Navigate to="/" replace />;
  }

  // Проверить роль (если требуется)
  if (requiredRole && user) {
    if (!user.roles.includes(requiredRole)) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
